import React, { useState, useEffect } from 'react';
import { StyleSheet, ScrollView, Keyboard } from 'react-native';
import { XStack, YStack } from '@tamagui/stacks';
import { Sheet } from '@tamagui/sheet';
import { Text } from '@tamagui/core';
import { COLORS } from '@/constants/colors';
import { CustomButton } from '@/components';
import { GIFTS } from '@/data/giftsData';
import GiftItem from './GiftItem';

type GiftSheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isDark?: boolean;
  onGiftSend?: (giftData: {
    giftId: string;
    giftName: string;
    giftEmoji: string;
    amount: number;
  }) => void;
};

// Emoji mapping for gifts
const GIFT_EMOJIS: Record<string, string> = {
  '1': '☕', // Kawa
  '2': '🌹', // Róża
  '3': '🍫', // Czekolada
  '4': '🎆', // Fajerwerki
  '5': '🌍', // Globus/Wycieczka
  '6': '💐', // Kwiaty
  '7': '🧸', // Miś
};

const GiftSheet = React.memo<GiftSheetProps>(({ open, onOpenChange, isDark, onGiftSend }) => {
  const [selectedGift, setSelectedGift] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  
  const bgColor = isDark ? COLORS.BLACK : COLORS.WHITE;
  const textColor = isDark ? COLORS.WHITE : COLORS.DARK_TEXT;
  const subtextColor = isDark ? COLORS.GRAY_400 : COLORS.GRAY_500;

  const digitalGifts = GIFTS.filter(g => g.type === 'digital');
  const physicalGifts = GIFTS.filter(g => g.type === 'physical');

  // Dismiss keyboard when sheet opens
  useEffect(() => {
    if (open) {
      Keyboard.dismiss();
    }
  }, [open]);

  // Reset selection when sheet closes
  useEffect(() => {
    if (!open) {
      setSelectedGift(null);
      setIsSending(false);
    }
  }, [open]);

  const handleSend = async () => {
    if (selectedGift && onGiftSend) {
      const gift = GIFTS.find(g => g.id === selectedGift);
      if (gift) {
        setIsSending(true);
        try {
          await onGiftSend({
            giftId: gift.id,
            giftName: gift.name,
            giftEmoji: GIFT_EMOJIS[gift.id] || '🎁',
            amount: gift.price,
          });
        } finally {
          setIsSending(false);
        }
      }
    } else if (selectedGift) {
      // Fallback for old usage without onGiftSend
      const gift = GIFTS.find(g => g.id === selectedGift);
      console.log('Sending gift:', gift);
      onOpenChange(false);
      setSelectedGift(null);
    }
  };

  return (
    <Sheet
      modal
      open={open}
      onOpenChange={onOpenChange}
      snapPoints={[50]}
      dismissOnSnapToBottom
    >
      <Sheet.Overlay backgroundColor="rgba(0, 0, 0, 0.5)" />
      <Sheet.Frame backgroundColor={bgColor} borderTopLeftRadius={24} borderTopRightRadius={24}>
        <Sheet.Handle backgroundColor={subtextColor} />
        <ScrollView style={styles.scrollView}>
          <YStack padding="$4" gap="$4" paddingBottom="$8">
            {/* Title */}
            <Text fontSize={20} fontWeight="700" color={textColor} textAlign="center">
              Wybierz prezent:
            </Text>

            {/* Digital Gifts */}
            <YStack gap="$3">
              <Text fontSize={20} fontWeight="600" color={textColor}>
                Prezenty cyfrowe:
              </Text>
              <XStack flexWrap="wrap" gap="$2" justifyContent='space-evenly'>
                {digitalGifts.map((gift) => (
                  <GiftItem
                    key={gift.id}
                    gift={gift}
                    isSelected={selectedGift === gift.id}
                    onPress={() => setSelectedGift(gift.id)}
                    isDark={isDark}
                  />
                ))}
              </XStack>
            </YStack>

            {/* Physical Gifts */}
            <YStack gap="$3" marginTop="$2">
              <Text fontSize={20} fontWeight="600" color={textColor}>
                Prezenty fizyczne:
              </Text>
              <XStack flexWrap="wrap" gap="$3" justifyContent="space-evenly">
                {physicalGifts.map((gift) => (
                  <GiftItem
                    key={gift.id}
                    gift={gift}
                    isSelected={selectedGift === gift.id}
                    onPress={() => setSelectedGift(gift.id)}
                    isDark={isDark}
                  />
                ))}
              </XStack>
            </YStack>

            {/* Send Button */}
            <YStack marginTop="$4" paddingHorizontal="$4">
              <CustomButton
                onPress={handleSend}
                disabled={!selectedGift || isSending}
                color={COLORS.WHITE}
                backgroundColor={COLORS.PRIMARY}
                paddingHorizontal="$4"
                paddingVertical="$3"
                borderRadius={12}
              >
                <Text fontSize={16} fontWeight="700" color={COLORS.WHITE}>
                  {isSending ? 'Wysyłanie...' : 'Zapłać i wyślij prezent'}
                </Text>
              </CustomButton>
            </YStack>
          </YStack>
        </ScrollView>
      </Sheet.Frame>
    </Sheet>
  );
});

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
});

GiftSheet.displayName = 'GiftSheet';

export default GiftSheet;


